import type {FC, PropsWithChildren} from 'react'
import {useEffect, useState} from 'react'
import {getBrandColorClasses} from '@/_new-code/products/pdp/utils/brand-colors'
import {useWindowSize} from '@/_new-code/utilities/hooks/use-window-size'

interface ModalProps {
	size: 'sm' | 'md' | 'lg' | 'xl'
	title?: string
	titleCenter?: boolean
	onClose: () => void
	variant?: 'center' | 'right-slide'
	isOpen?: boolean
	brandColor?: string
}

export const Modal: FC<PropsWithChildren<ModalProps>> = ({
	size,
	title = undefined,
	titleCenter = false,
	onClose,
	children,
	variant = 'center',
	isOpen = true,
	brandColor,
}) => {
	const {isMobile} = useWindowSize()

	const modalSizes = {
		sm: 'max-w-sm',
		md: 'max-w-md',
		lg: 'max-w-xl',
		xl: 'max-w-3xl',
	}

	// Right slide modal state management
	const [shouldRender, setShouldRender] = useState(variant === 'center')
	const [isAnimating, setIsAnimating] = useState(variant === 'center')

	// Brand color classes for right slide variant
	const brandColorClasses = getBrandColorClasses(brandColor)
	const brandTextClass = brandColorClasses.text
	const closeButtonStyle =
		brandColor && brandColor.startsWith('#')
			? {color: brandColor}
			: undefined

	// Animation effects for right slide modal
	useEffect(() => {
		if (variant !== 'right-slide') return

		let raf1 = 0
		let raf2 = 0
		let closeTimeout: number | undefined

		if (isOpen) {
			setShouldRender(true)
			raf1 = requestAnimationFrame(() => {
				raf2 = requestAnimationFrame(() => {
					setIsAnimating(true)
				})
			})
		} else {
			setIsAnimating(false)
			closeTimeout = window.setTimeout(() => {
				setShouldRender(false)
			}, 300)
		}

		return () => {
			if (raf1) cancelAnimationFrame(raf1)
			if (raf2) cancelAnimationFrame(raf2)
			if (closeTimeout) clearTimeout(closeTimeout)
		}
	}, [isOpen, variant])

	// Keyboard and body scroll management
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent): void => {
			if (e.key === 'Escape' && isOpen) {
				onClose()
			}
		}

		if (isOpen) {
			document.addEventListener('keydown', handleKeyDown)
			if (variant === 'right-slide') {
				document.body.style.overflow = 'hidden'
			}
			return () => {
				document.removeEventListener('keydown', handleKeyDown)
				if (variant === 'right-slide') {
					document.body.style.overflow = 'unset'
				}
			}
		}
	}, [isOpen, onClose, variant])

	// Don't render if right-slide variant and not ready
	if (variant === 'right-slide' && !shouldRender) return null

	// Right slide modal variant
	if (variant === 'right-slide') {
		return (
			<div className="fixed inset-0 z-[9999] overflow-hidden">
				{/* Backdrop */}
				<div
					aria-hidden="true"
					className={`absolute inset-0 bg-black transition-opacity duration-300 ${
						isAnimating ? 'bg-opacity-50' : 'bg-opacity-0'
					}`}
					onClick={onClose}
					onKeyDown={(e) => {
						if (e.key === 'Enter' || e.key === ' ') onClose()
					}}
					role="button"
					tabIndex={0}
				/>

				{/* Modal Panel */}
				<div
					className={`absolute right-0 top-0 flex h-full w-full max-w-full transform flex-col bg-white pt-0 shadow-xl transition-transform duration-300 ease-in-out sm:max-w-lg md:max-w-xl lg:max-w-2xl ${
						isAnimating ? 'translate-x-0' : 'translate-x-full'
					}`}
				>
					<div
						className={`relative mt-2 px-4 pb-6 sm:mt-4 sm:px-6 sm:pb-8 md:px-8 md:pb-10 ${
							isMobile ? 'mr-2 sm:mr-4' : 'ml-2 sm:ml-4'
						}`}
					>
						<button
							aria-label="Close"
							className={`${brandTextClass} absolute top-0 transition-colors focus:outline-none ${
								isMobile ? 'right-2' : 'left-2'
							}`}
							onClick={onClose}
							style={closeButtonStyle}
							type="button"
						>
							<svg
								className="h-8 w-8 sm:h-10 sm:w-10 md:h-12 md:w-12"
								fill="none"
								stroke="currentColor"
								strokeWidth={1}
								viewBox="0 0 24 24"
							>
								<path
									d="M6 18L18 6M6 6l12 12"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						</button>
					</div>

					{/* Header */}
					{title ? (
						<div className="px-8 py-3 sm:px-8 sm:py-4 md:px-8">
							<h2 className="text-gray-900 text-[24px] font-bold leading-none">
								{title}
							</h2>
						</div>
					) : null}

					{/* Content */}
					<div className="flex-1 overflow-y-auto px-8 pb-8 pt-10 sm:px-8 sm:pb-10 sm:pt-6 md:px-8 md:pb-12 md:pt-8">
						{children}
					</div>
				</div>
			</div>
		)
	}

	// Default center modal variant (preserves existing functionality)
	return (
		<div
			aria-modal
			className="fixed bottom-0 left-0 right-0 top-0 z-[1000] flex items-center justify-center bg-black/50"
		>
			<div
				className={`${modalSizes[size]} m-6 h-fit max-h-[90%] overflow-y-auto rounded border-light-grey bg-white shadow-lg`}
			>
				<div className="mr-4 mt-4 flex justify-end">
					<button onClick={onClose} type="button">
						<span aria-label="Close">
							<svg
								height="19.784"
								viewBox="0 0 19.789 19.784"
								width="19.789"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M23.524,21.181l7.068-7.068a1.656,1.656,0,1,0-2.342-2.342l-7.068,7.068-7.068-7.068a1.656,1.656,0,1,0-2.342,2.342l7.068,7.068-7.068,7.068a1.656,1.656,0,0,0,2.342,2.342l7.068-7.068,7.068,7.068a1.656,1.656,0,0,0,2.342-2.342Z"
									data-name="Icon ionic-ios-close"
									fill="#021546"
									transform="translate(-11.285 -11.289)"
								/>
							</svg>
						</span>
					</button>
				</div>
				<div className="p-8 pt-2">
					{title ? (
						<h2
							className={`text-pet-primary ${
								titleCenter ? 'text-center' : 'text-left'
							} mb-4 text-2xl font-bold`}
						>
							{title}
						</h2>
					) : null}
					{children}
				</div>
			</div>
		</div>
	)
}
